import { useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, DashboardModule, Form, ModalContext, SectionDivider, SidebarModal, Text,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useAgencyProviderPackage from '@hooks/useAgencyProviderPackage'
import useProviderPackageItems from '@hooks/useProviderPackageItems'

import { AgencyProviderPackageModel } from '@models/types'

import ProviderPackageItemListItem from './components/ProviderPackageItemListItem'

const MODAL_KEY = 'ManageAgencyProviderPackageModal'

interface ModalCallbacks {
  closeModal: () => void,
  updateAgencyProviderPackage: (toggleVisibilityPayload: HandleCallbackActionParams) => void,
}

type ConfirmToggleVisibilityParams = {
  agencyProviderPackage: AgencyProviderPackageModel,
  callbacks: ModalCallbacks,
  toggleFn: () => void,
}

const confirmToggleVisibility = (params: ConfirmToggleVisibilityParams) => {
  const {
    agencyProviderPackage, agencyProviderPackage: { hidden }, callbacks, toggleFn,
  } = params
  const { closeModal, updateAgencyProviderPackage } = callbacks

  const toggleVisibilityPayload = {
    callbacks: {
      action: toggleFn,
      afterAction: closeModal,
    },
    entityParams: agencyProviderPackage,
    toastText: 'Package Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: `Yes, ${hidden ? 'show' : 'hide'} it.`,
      icon: 'warning',
      showCancelButton: true,
      title: `${hidden ? 'Show' : 'Hide'} Package?`,
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        updateAgencyProviderPackage(toggleVisibilityPayload)
      }
    })
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageAgencyProviderPackageModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agencyProviderPackage: initAgencyProviderPackage } = modalPayload

  const agencyProviderPackagePayload = useAgencyProviderPackage(initAgencyProviderPackage)
  const {
    agencyProviderPackage,
    agencyProviderPackage: {
      hidden,
      id: agencyProviderPackageId,
      provider_package_id,
    },
    callbacks: {
      toggleVisibility: toggleFn,
    },
    providerPackage: {
      // @ts-expect-error TS2339
      title: providerPackageTitle,
    },
  } = agencyProviderPackagePayload

  const providerPackageItemsPayload = useProviderPackageItems({
    filters: {
      provider_package_id,
    },
    performHttpRequests: !!agencyProviderPackageId,
    requestOptions: {
      include_provider_package_items: true,
      include_provider_package_item_price_list: true,
    },
  })
  const {
    hasFilteredProviderPackageItems,
    filteredProviderPackageItems,
  } = providerPackageItemsPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="large">
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Package" />

      <SidebarModal.SubHeader>
        <Box flexDirection="column">
          <Text>{providerPackageTitle}</Text>

          <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
            {`# ${provider_package_id}`}
          </Text>
        </Box>
      </SidebarModal.SubHeader>

      {/*
       // @ts-expect-error TS2322 */}
      <SidebarModal.Content hasSubheader>
        {hasFilteredProviderPackageItems && (
          <>
            <SectionDivider>ITEMS</SectionDivider>

            {/*
             // @ts-expect-error TS2322 */}
            <DashboardModule.ScrollableSection maxHeight={600}>
              {filteredProviderPackageItems.map(item => (
                <ProviderPackageItemListItem
                  key={item.id}
                  providerPackageItem={item}
                />
              ))}
            </DashboardModule.ScrollableSection>
          </>
        )}

        <Form.Field label="More Options" marginTop="large">
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={hidden ? faEye : faEyeSlash} />}
            onClick={() => confirmToggleVisibility({
              agencyProviderPackage, callbacks, toggleFn,
            })}
            size="medium"
          >
            {`${hidden ? 'Show' : 'Hide'} Package`}
          </Button>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencyProviderPackageModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
